/**
 * Authentication Context
 *
 * Provides authentication state and methods throughout the application.
 * Provides authentication state and methods throughout the application.
 * Handles Styx sign-in (TRACI-474), the only staff sign-in since TRACI-620
 * removed the old Cognito/Duo one, plus session management and access control.
 *
 * Supports VITE_AUTH_DISABLED=true for local development without sign-in.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import {
  readStyxSession, readStoredSession, clearStyxSession, expiryOf, fetchAuthConfig,
  isStyxCallback, revokeStyxSession, endSessionUrl,
} from '../auth/styx';
import { getBackendApiUrl } from '../config/backend';
import { logger } from '../utils/logger';
import { clearStaffSiteToken } from '../utils/staffSiteToken';

// Check if authentication is disabled (for local development without sign-in)
const AUTH_DISABLED = import.meta.env.VITE_AUTH_DISABLED === 'true';

interface User {
  username: string;
  userId: string;
  attributes: Record<string, unknown>;
}

interface UserDetails {
  role: 'Admin' | 'QA' | 'User';
  status: string;
  email: string;
  userId: string;
  lastLoginAt?: string;
}

interface AccessCheck {
  allowed: boolean;
  reason?: string;
}

interface AuthContextValue {
  user: User | null;
  userDetails: UserDetails | null;
  accessCheck: AccessCheck | null;
  loading: boolean;
  error: string | null;
  sessionExpiry: number | null;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
  /** Re-read the stored session and load the user (the Styx callback calls this after it signs in). */
  reloadUser: () => Promise<void>;
  getToken: () => Promise<string | undefined>;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isQA: boolean;
  isBlocked: boolean;
  blockReason?: string;
  isAuthDisabled: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

// Mock user for when auth is disabled (local development)
const MOCK_USER: User = {
  username: 'local-dev-user',
  userId: 'local-dev-user-id',
  attributes: {
    email: 'localdev@example.com',
    email_verified: true,
  },
};

const MOCK_USER_DETAILS: UserDetails = {
  role: 'Admin', // Give full access in local dev
  status: 'active',
  email: 'localdev@example.com',
  userId: 'local-dev-user-id',
};

const MOCK_ACCESS_CHECK: AccessCheck = {
  allowed: true,
};

/**
 * Clear Amplify auth state left in localStorage by the retired Cognito/Duo
 * sign-in (TRACI-620). Those keys are never a session any more; dropping them
 * keeps an old id token from lingering in the browser.
 */
const clearAmplifyAuthKeys = () => {
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (
      key.includes('CognitoIdentityServiceProvider') ||
      key.startsWith('amplify-signin-with-hostedUI')
    )) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(key => localStorage.removeItem(key));
  if (keysToRemove.length > 0) {
    logger.debug(`[AUTH] Cleared ${keysToRemove.length} stale Amplify auth keys`);
  }
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(AUTH_DISABLED ? MOCK_USER : null);
  const [userDetails, setUserDetails] = useState<UserDetails | null>(AUTH_DISABLED ? MOCK_USER_DETAILS : null);
  const [accessCheck, setAccessCheck] = useState<AccessCheck | null>(AUTH_DISABLED ? MOCK_ACCESS_CHECK : null);
  const [loading, setLoading] = useState(!AUTH_DISABLED); // Not loading if auth disabled
  const [error, setError] = useState<string | null>(null);
  const [sessionExpiry, setSessionExpiry] = useState<number | null>(null);

  /**
   * Clear local auth state (no redirect). Safe to call from programmatic
   * cleanup paths (stale token, expired session).
   */
  const clearLocalAuthState = () => {
    clearAmplifyAuthKeys();
    clearStyxSession();
    setUser(null);
    setUserDetails(null);
    setAccessCheck(null);
    setSessionExpiry(null);
    localStorage.removeItem('sessionExpiry');
  };

  // If auth is disabled, fetch the current mock user from the backend
  // This is set by the user switcher dropdown in the AuthDisabledBanner
  const [mockRole, setMockRole] = useState<string | null>(null);
  const [mockEmail, setMockEmail] = useState<string>('localdev@example.com');
  // TRACI-1189: the role always comes from the backend, so the page shows the role the
  // backend really sees. The dev panel's role switch calls reloadUser() after it switches.
  const loadMockRole = () =>
    fetch(`${getBackendApiUrl()}/api/users/mock-role`, {
      headers: { Authorization: 'Bearer auth-disabled-mock-token' },
    })
      .then(r => {
        if (!r.ok) throw new Error(`mock-role ${r.status}`);
        return r.json();
      })
      .then(d => {
        setMockRole(d.role || 'Admin');
        if (d.email) setMockEmail(d.email);
      })
      .catch(() => setMockRole(prev => prev ?? 'Admin'));
  // Bumped by a role switch only, so the first load does not remount the page.
  const [mockRoleEpoch, setMockRoleEpoch] = useState(0);
  const reloadMockRole = async () => {
    await loadMockRole();
    setMockRoleEpoch(e => e + 1);
  };
  useEffect(() => {
    if (AUTH_DISABLED) loadMockRole();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (AUTH_DISABLED) {
    // Don't render until we know the mock role
    if (mockRole === null) {
      const loadingValue: AuthContextValue = {
        user: null, userDetails: null, accessCheck: null,
        loading: true, error: null, sessionExpiry: null,
        logout: async () => {}, refreshSession: async () => {}, reloadUser: async () => {},
        getToken: async () => undefined,
        isAuthenticated: false, isAdmin: false, isQA: false,
        isBlocked: false, blockReason: undefined, isAuthDisabled: true,
      };
      return (
        <AuthContext.Provider value={loadingValue}>
          <React.Fragment key={mockRoleEpoch}>{children}</React.Fragment>
        </AuthContext.Provider>
      );
    }

    const mockDetails = { ...MOCK_USER_DETAILS, role: mockRole, email: mockEmail };
    const mockValue: AuthContextValue = {
      user: MOCK_USER,
      userDetails: mockDetails,
      accessCheck: MOCK_ACCESS_CHECK,
      loading: false,
      error: null,
      sessionExpiry: null,
      logout: async () => { logger.debug('Auth disabled - logout is a no-op'); },
      refreshSession: async () => { logger.debug('Auth disabled - refresh is a no-op'); },
      reloadUser: reloadMockRole,
      getToken: async () => 'auth-disabled-mock-token',
      isAuthenticated: true,
      isAdmin: mockRole === 'Admin',
      isQA: mockRole === 'QA',
      isBlocked: false,
      blockReason: undefined,
      isAuthDisabled: true,
    };

    // Keyed by switch count: a role switch remounts the page, so every screen reloads its
    // data as the new role without a browser reload.
    return (
      <AuthContext.Provider value={mockValue}>
        <React.Fragment key={mockRoleEpoch}>{children}</React.Fragment>
      </AuthContext.Provider>
    );
  }

  /**
   * Fetch user details from backend API
   */
  const fetchUserDetails = useCallback(async (token: string): Promise<UserDetails | null> => {
    try {
      const response = await fetch(`${getBackendApiUrl()}/api/users/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUserDetails(data);
        return data;
      } else if (response.status === 401 || response.status === 403 || response.status === 404) {
        // User doesn't exist, not authorized, or token is invalid - clear session
        logger.warn('User not found or unauthorized - logging out');
        throw new Error('User not found or unauthorized');
      }
    } catch (err) {
      logger.error('Error fetching user details:', err);
      throw err;
    }
    return null;
  }, []);

  /**
   * Check if user has access to the application
   */
  const checkAccess = useCallback(async (token: string): Promise<AccessCheck> => {
    try {
      const response = await fetch(`${getBackendApiUrl()}/api/users/me/access`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setAccessCheck(data);
        return data;
      }
    } catch (err) {
      logger.error('Error checking access:', err);
    }
    return { allowed: false, reason: 'Failed to check access' };
  }, []);

  /**
   * TRACI-474: load the person behind a `trs_` session token. A 401/403/404 from
   * the backend means the session is gone (logged out elsewhere, row removed):
   * drop it so the next render lands on the sign-in page.
   */
  const loadStyxUser = useCallback(async (token: string, expiry: number | null) => {
    try {
      const details = await fetchUserDetails(token);
      if (!details) {
        setUser(null);
        return;
      }
      await checkAccess(token);
      setUser({
        username: details.email,
        userId: details.userId,
        attributes: { email: details.email },
      });
      setSessionExpiry(expiry);
    } catch (err) {
      // Only a 401/403/404 (fetchUserDetails throws this message) ends the session;
      // a network blip keeps the stored token so the next load can retry.
      const rejected = err instanceof Error && err.message === 'User not found or unauthorized';
      if (rejected) {
        logger.error('Styx session rejected by the backend, signing out locally');
        clearStyxSession();
      } else {
        logger.error('Could not load the Styx session user, keeping the session for a retry');
      }
      setUser(null);
      setUserDetails(null);
      setAccessCheck(null);
      setSessionExpiry(null);
    }
  }, [fetchUserDetails, checkAccess]);

  /**
   * Load current user session
   */
  const loadUser = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // TRACI-474: the Styx session is the only sign-in.
      const styx = readStyxSession();
      if (styx) {
        await loadStyxUser(styx.token, expiryOf(styx));
        return;
      }
      // A lapsed one (8 h, no refresh) means signed out: drop it. ProtectedRoute
      // then sends the person to /login with the page they were on as the return path.
      if (readStoredSession()) clearStyxSession();
      // The return leg of a Styx sign-in: the Callback page finishes it and then
      // asks us to reload, so there is nothing to restore yet.
      if (isStyxCallback()) {
        setUser(null);
        setUserDetails(null);
        setAccessCheck(null);
        setSessionExpiry(null);
        return;
      }

      // TRACI-620: the old Cognito/Duo session is no longer a sign-in. Drop any
      // keys it left behind; ProtectedRoute sends the person to /login.
      clearAmplifyAuthKeys();
      localStorage.removeItem('sessionExpiry');
      setUser(null);
      setUserDetails(null);
      setAccessCheck(null);
      setSessionExpiry(null);
    } catch (err) {
      logger.error('Error loading user:', err);
      setUser(null);
      setUserDetails(null);
      setAccessCheck(null);
      setSessionExpiry(null);
    } finally {
      setLoading(false);
    }
  }, [fetchUserDetails, checkAccess, loadStyxUser]);

  /**
   * Sign out
   */
  const logout = async (): Promise<void> => {
    // CPOC-105: signing out also ends Connect mode on tirerack.com in this browser, so
    // the next person on a shared machine never chats there as this staff member.
    clearStaffSiteToken();
    // TRACI-474: a Styx session ends at the backend, then at Styx itself.
    const stored = readStoredSession();
    if (stored) {
      // Clear locally first so an offline backend can never leave the token behind.
      clearLocalAuthState();
      const [cfg] = await Promise.all([fetchAuthConfig(), revokeStyxSession(stored.token)]);
      if (cfg.styx.enabled && cfg.styx.issuer) {
        window.location.assign(endSessionUrl(cfg));
      }
      return;
    }
    clearLocalAuthState();
  };

  /**
   * Refresh user session and access check
   */
  const refreshSession = useCallback(async (): Promise<void> => {
    try {
      const styx = readStyxSession();
      if (!styx) return;
      await fetchUserDetails(styx.token);
      await checkAccess(styx.token);
    } catch (err) {
      logger.error('Error refreshing session:', err);
    }
  }, [fetchUserDetails, checkAccess]);

  /**
   * Get current JWT token
   */
  const getToken = async (): Promise<string | undefined> => {
    try {
      // TRACI-474: the Styx session token, read straight from storage.
      return readStyxSession()?.token;
    } catch (err) {
      logger.error('Error getting token:', err);
      return undefined;
    }
  };

  // Check session expiry on mount and set up interval
  useEffect(() => {
    const checkSessionExpiry = async () => {
      // A Styx session has no refresh: when its 8 h are up the person is signed out.
      const styxStored = readStoredSession();
      if (styxStored) {
        const exp = expiryOf(styxStored);
        if (exp === null || Date.now() >= exp) {
          logger.debug('Styx session expired, signing out locally');
          clearLocalAuthState();
        }
        return;
      }
    };

    checkSessionExpiry();

    // Check every minute
    const interval = setInterval(checkSessionExpiry, 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  // Load user on mount
  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const value: AuthContextValue = {
    user,
    userDetails,
    accessCheck,
    loading,
    error,
    sessionExpiry,
    logout,
    refreshSession,
    reloadUser: loadUser,
    getToken,
    isAuthenticated: !!user,
    isAdmin: userDetails?.role === 'Admin',
    isQA: userDetails?.role === 'QA',
    isBlocked: !accessCheck?.allowed,
    blockReason: accessCheck?.reason,
    isAuthDisabled: false,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
